import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

const KEY = "rrz-program-reminders";
const FIRED_KEY = "rrz-program-reminders-fired";

/**
 * Hook: per-program reminders.
 *
 * Flow:
 *   - User toggles a program → saved into localStorage (set of names).
 *   - A dedicated interval scans the schedule every 30 s.
 *   - If a reminded program starts within the next 1-2 minutes (and we have
 *     not already notified today for that slot) we fire a local Notification
 *     (requesting permission the first time) and an in-app toast.
 *   - Fired keys are stored per-day to avoid duplicate alerts, and reset at
 *     midnight.
 */
export default function useProgramReminders(slots) {
  const [reminders, setReminders] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(KEY) || "[]")); }
    catch { return new Set(); }
  });

  const slotsRef = useRef(slots || []);
  useEffect(() => { slotsRef.current = slots || []; }, [slots]);

  // Persist
  useEffect(() => {
    try { localStorage.setItem(KEY, JSON.stringify(Array.from(reminders))); } catch {}
  }, [reminders]);

  const todayKey = () => new Date().toISOString().slice(0, 10); // YYYY-MM-DD

  const loadFired = () => {
    try {
      const raw = JSON.parse(localStorage.getItem(FIRED_KEY) || "null");
      if (raw && raw.day === todayKey()) return new Set(raw.ids);
    } catch {}
    return new Set();
  };
  const saveFired = (set) => {
    try { localStorage.setItem(FIRED_KEY, JSON.stringify({ day: todayKey(), ids: Array.from(set) })); } catch {}
  };

  // Scan loop
  useEffect(() => {
    let stop = false;
    const scan = async () => {
      if (stop) return;
      const set = reminders;
      if (set.size === 0) return;
      const now = new Date();
      const nowMin = now.getHours() * 60 + now.getMinutes();
      const fired = loadFired();
      const scheduleSlots = slotsRef.current || [];
      for (const s of scheduleSlots) {
        if (s.is_meteo) continue;
        if (!set.has(s.program)) continue;
        const diff = s.start_min - nowMin;
        if (diff >= 0 && diff <= 2) {
          const id = `${todayKey()}|${s.start}|${s.program}`;
          if (fired.has(id)) continue;
          fired.add(id);
          saveFired(fired);
          fireNotification(s);
        }
      }
    };
    const id = setInterval(scan, 30 * 1000);
    scan();
    return () => { stop = true; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reminders]);

  const toggle = async (programName) => {
    const already = reminders.has(programName);
    const next = new Set(reminders);
    if (already) {
      next.delete(programName);
      toast.info(`Promemoria rimosso: ${programName}`);
    } else {
      // Ask for notification permission on first add
      if ("Notification" in window && Notification.permission === "default") {
        try { await Notification.requestPermission(); } catch {}
      }
      next.add(programName);
      if ("Notification" in window && Notification.permission === "granted") {
        toast.success(`Ti avviseremo quando parte: ${programName}`);
      } else {
        toast.success(`Promemoria attivo: ${programName}`, {
          description: "Attiva le notifiche del browser per riceverlo anche a schermo spento.",
        });
      }
    }
    setReminders(next);
  };

  return { reminders, toggle };
}

function fireNotification(slot) {
  const title = "Sta per iniziare";
  const body = `${slot.program} alle ${slot.start}`;
  // In-app toast always
  toast.success(title, { description: body, duration: 15000 });
  // System notification (if permitted) via service worker so it shows even when the tab is in background
  try {
    if ("serviceWorker" in navigator && "Notification" in window && Notification.permission === "granted") {
      navigator.serviceWorker.getRegistration("/sw.js").then((reg) => {
        if (!reg) {
          try { new Notification(title, { body, icon: "/icon-192.png", tag: `rrz-reminder-${slot.start}` }); } catch {}
          return;
        }
        reg.showNotification(title, {
          body,
          icon: "/icon-192.png",
          badge: "/icon-192.png",
          tag: `rrz-reminder-${slot.start}`,
          renotify: false,
        });
      });
    }
  } catch {}
}
